import { ResolverError } from './errors';
import type {
	AuthorizationServerMetadata,
	DidDocument,
	Did,
	IdentityMetadata,
	ProtectedResourceMetadata,
} from './types';
import { extractContentType, isValidUrl } from './utils';

/**
 * AppView used purely for identity resolution (`resolveHandle`). This is an
 * unauthenticated, public endpoint.
 */
export const DEFAULT_APPVIEW_URL = 'https://public.api.bsky.app';

const DID_WEB_RE = /^([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*(?:\.[a-zA-Z]{2,}))$/;
const HANDLE_RE = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

export const isDid = (value: string): value is Did => {
	return (
		value.length > 4 &&
		value.startsWith('did:') &&
		// did:<method>:<id>
		value.indexOf(':', 4) > 4
	);
};

export const isHandle = (value: string): boolean => {
	return HANDLE_RE.test(value) && value.length <= 253;
};

/**
 * Retrieves the AT Protocol PDS endpoint from a DID document, if available.
 *
 * Vendored from `@atcute/client/utils/did` so that this module stays free of
 * runtime dependencies and remains unit-testable in isolation.
 */
export const getPdsEndpoint = (doc: DidDocument): string | undefined => {
	return getServiceEndpoint(doc, '#atproto_pds', 'AtprotoPersonalDataServer');
};

export const getServiceEndpoint = (
	doc: DidDocument,
	serviceId: string,
	serviceType: string,
): string | undefined => {
	const did = doc.id;

	const didServiceId = did + serviceId;
	const found = doc.service?.find((service) => service.id === serviceId || service.id === didServiceId);

	if (!found || found.type !== serviceType || typeof found.serviceEndpoint !== 'string') {
		return undefined;
	}

	return validateUrl(found.serviceEndpoint);
};

const validateUrl = (urlStr: string): string | undefined => {
	let url;
	try {
		url = new URL(urlStr);
	} catch {
		return undefined;
	}

	const proto = url.protocol;
	if (url.hostname && (proto === 'http:' || proto === 'https:')) {
		return urlStr;
	}

	return undefined;
};

/**
 * Resolves a domain handle into a DID identifier by asking the public AppView.
 * @param handle Domain handle to resolve
 * @returns DID identifier resolved from the domain handle
 */
export const resolveHandle = async (handle: string, signal?: AbortSignal): Promise<Did> => {
	if (!isHandle(handle)) {
		throw new ResolverError(`invalid handle`);
	}

	const url =
		DEFAULT_APPVIEW_URL + `/xrpc/com.atproto.identity.resolveHandle?handle=${encodeURIComponent(handle)}`;

	const response = await fetch(url, { signal });
	if (response.status === 400) {
		throw new ResolverError(`domain handle not found`);
	} else if (!response.ok) {
		throw new ResolverError(`directory is unreachable`);
	}

	const json = (await response.json()) as { did?: string };
	const did = json?.did;

	if (typeof did !== 'string' || !isDid(did)) {
		throw new ResolverError(`invalid resolution response`);
	}

	return did;
};

/**
 * Gets the DID document of a `did:plc` (via plc.directory) or `did:web` identifier.
 * @param did DID identifier we're seeking the DID document for
 * @returns Retrieved DID document
 */
export const getDidDocument = async (did: Did, signal?: AbortSignal): Promise<DidDocument> => {
	const colonIndex = did.indexOf(':', 4);

	const type = did.slice(4, colonIndex);
	const ident = did.slice(colonIndex + 1);

	let doc: DidDocument;

	if (type === 'plc') {
		// The DID is used verbatim as a path segment; its colons are URL-safe.
		const response = await fetch(`https://plc.directory/${did}`, { signal });

		if (response.status === 404) {
			throw new ResolverError(`did not found in directory`);
		} else if (!response.ok) {
			throw new ResolverError(`directory is unreachable`);
		}

		doc = (await response.json()) as DidDocument;
	} else if (type === 'web') {
		if (!DID_WEB_RE.test(ident)) {
			throw new ResolverError(`invalid did:web identifier`);
		}

		const response = await fetch(`https://${ident}/.well-known/did.json`, { signal });

		if (!response.ok) {
			throw new ResolverError(`did document is unreachable`);
		}

		doc = (await response.json()) as DidDocument;
	} else {
		throw new ResolverError(`unsupported did method`);
	}

	if (!doc || typeof doc !== 'object' || doc.id !== did) {
		throw new ResolverError(`did document mismatch`);
	}

	return doc;
};

/**
 * Gets OAuth protected resource metadata from a host.
 */
export const getProtectedResourceMetadata = async (
	host: string,
	signal?: AbortSignal,
): Promise<ProtectedResourceMetadata> => {
	const url = new URL(`/.well-known/oauth-protected-resource`, host);
	const response = await fetch(url, {
		signal,
		redirect: 'manual',
		headers: { accept: 'application/json' },
	});

	if (response.status !== 200 || extractContentType(response.headers) !== 'application/json') {
		throw new ResolverError(`unexpected response from protected resource metadata`);
	}

	const metadata = (await response.json()) as ProtectedResourceMetadata;
	if (metadata.resource !== url.origin) {
		throw new ResolverError(`unexpected resource in protected resource metadata`);
	}

	return metadata;
};

/**
 * Gets OAuth authorization server metadata from a host.
 */
export const getAuthorizationServerMetadata = async (
	host: string,
	signal?: AbortSignal,
): Promise<AuthorizationServerMetadata> => {
	const url = new URL(`/.well-known/oauth-authorization-server`, host);
	const response = await fetch(url, {
		signal,
		redirect: 'manual',
		headers: { accept: 'application/json' },
	});

	if (response.status !== 200 || extractContentType(response.headers) !== 'application/json') {
		throw new ResolverError(`unexpected response from authorization server metadata`);
	}

	const metadata = (await response.json()) as AuthorizationServerMetadata;

	if (metadata.issuer !== url.origin) {
		throw new ResolverError(`unexpected issuer in authorization server metadata`);
	}
	if (!isValidUrl(metadata.authorization_endpoint)) {
		throw new ResolverError(`authorization server provided incorrect authorization endpoint`);
	}
	if (!metadata.client_id_metadata_document_supported) {
		throw new ResolverError(`authorization server does not support 'client_id_metadata_document'`);
	}
	if (!metadata.pushed_authorization_request_endpoint) {
		throw new ResolverError(`authorization server does not support pushed authorization requests`);
	}
	if (metadata.response_types_supported && !metadata.response_types_supported.includes('code')) {
		throw new ResolverError(`authorization server does not support the 'code' response type`);
	}

	return metadata;
};

/**
 * Resolve handle domains or DID identifiers to get their PDS and its
 * authorization server metadata.
 * @param ident Handle domain or DID identifier to resolve
 */
export const resolveFromIdentity = async (
	ident: string,
	signal?: AbortSignal,
): Promise<{ identity: IdentityMetadata; metadata: AuthorizationServerMetadata }> => {
	let did: Did;
	if (isDid(ident)) {
		did = ident;
	} else {
		did = await resolveHandle(ident, signal);
	}

	const doc = await getDidDocument(did, signal);
	const pds = getPdsEndpoint(doc);

	if (!pds) {
		throw new ResolverError(`missing pds endpoint`);
	}

	return {
		identity: {
			id: did,
			raw: ident,
			pds: new URL(pds),
		},
		metadata: await getMetadataFromResourceServer(pds, signal),
	};
};

/**
 * Request authorization server metadata directly from a service (PDS) URL.
 */
export const resolveFromService = async (
	host: string,
	signal?: AbortSignal,
): Promise<{ metadata: AuthorizationServerMetadata }> => {
	try {
		const metadata = await getMetadataFromResourceServer(host, signal);
		return { metadata };
	} catch (err) {
		if (err instanceof ResolverError) {
			try {
				const metadata = await getAuthorizationServerMetadata(host, signal);
				return { metadata };
			} catch {}
		}

		throw err;
	}
};

/**
 * Resolve authorization server metadata from a resource server's
 * (PDS) protected resource metadata.
 */
export const getMetadataFromResourceServer = async (
	input: string,
	signal?: AbortSignal,
): Promise<AuthorizationServerMetadata> => {
	const rsMetadata = await getProtectedResourceMetadata(input, signal);

	if (rsMetadata.authorization_servers?.length !== 1) {
		throw new ResolverError(`expected exactly one authorization server in the listing`);
	}

	const issuer = rsMetadata.authorization_servers[0];
	const asMetadata = await getAuthorizationServerMetadata(issuer, signal);

	if (asMetadata.protected_resources && !asMetadata.protected_resources.includes(rsMetadata.resource)) {
		throw new ResolverError(`server is not in authorization server's jurisdiction`);
	}

	return asMetadata;
};
